/**
 * Which Suggestion Engine CARDS a module shows, and what each is badged as — one rule for every
 * module on the aged care spine (owner, 24 Sep 2026: "harmonisation of the UI/UX"; 2 Oct 2026:
 * the downsizer and granny flat listed every candidate, the village showed one card per goal).
 *
 * Lifted from the village page's `seCards` / `visibleSeCards`, unchanged in substance, so the
 * downsizer and granny flat analysers cannot grow their own version:
 *   - one card per goal leader, badged with the client-facing chip words;
 *   - a goal with no improver shows its best-of-field, badged "Best available …" and said to fall
 *     short of the baseline, so a client who has decided to move still sees the least costly route;
 *   - a selected strategy that leads nothing still gets a card ("Your selection"), or the click
 *     reads as ignored;
 *   - the chip filters by badge; "cashflow covered" that is the same plan as "Best estate" shows
 *     the one-line note instead of the same card twice.
 */
import { BEST_ESTATE_CHIP, CASHFLOW_COVERED_CHIP, GOAL_CHIP_LABELS } from './cashflowCoveredGoal.ts';
import { NEAR_BEST_LABEL, type EvaluatedStrategy, type RecommendedStrategyEntry } from './supportAtHomeRecommendedStrategies.ts';
import { SECOND_PLAN_CHIP, secondPlanLine, secondPlanChip, leadChip, leadLine } from './planChoiceRule.ts';

export type GoalFocus = 'covered' | 'legacy' | 'cashflow' | 'pension';

export interface GoalCard<S extends EvaluatedStrategy = EvaluatedStrategy> {
    strategy: S;
    badges: string[];
    /** Set when this is the best of the field for a goal WITHOUT beating the baseline — said on the card. */
    belowBaseline?: string;
    /** Set on the second card beside the estate leader: the trade-off that earns it a place (planChoiceRule.ts). */
    tradeOffNote?: string;
}

/** The chip row, in the owner's order: Best estate first and the default, then cashflow covered. */
export const GOAL_FOCUS_CHIPS: ReadonlyArray<{ id: GoalFocus; label: string }> = [
    { id: 'legacy', label: BEST_ESTATE_CHIP },
    { id: 'covered', label: CASHFLOW_COVERED_CHIP },
    { id: 'cashflow', label: 'Best cashflow' },
    { id: 'pension', label: 'Best Age Pension' },
];

/** One line under the chips saying what the chosen goal means, with the module's baseline named. */
export function goalFocusExplanation(focus: GoalFocus, baselineWord: string): string {
    switch (focus) {
        case 'covered': return `The best estate among the plans that still pay the bills — or, where none does, whose shortfall the savings carry for the whole period. The card says which.`;
        case 'legacy': return `The strategy that leaves the most to the family after the projection period, against ${baselineWord}.`;
        case 'cashflow': return 'The strongest yearly cashflow from this run.';
        case 'pension': return 'The highest modelled Age Pension, where any strategy produces one.';
    }
}

export interface BuildGoalCardsInput<S extends EvaluatedStrategy> {
    /** Every evaluated strategy, the baseline included. */
    strategies: S[];
    /** The goal leaders, merged by name with their engine labels (mergeGoalLeaderStrategies). */
    goalLeaders: S[];
    /** The do-nothing candidate, for the "does not beat …" figures. */
    baseline: S | null | undefined;
    /** True for the candidate(s) that are the baseline, or otherwise never a card (a sensitivity). */
    isBaseline: (s: S) => boolean;
    /** Excluded from cards and from "best available" — price sensitivities, for one. */
    isSensitivity?: (s: S) => boolean;
    selectedId: string | null;
    /** "staying put", "not downsizing", "not entering the arrangement". */
    baselineWord: string;
    formatCurrency: (n: number) => string;
    /** The second card's one line, keyed by the plan it belongs to (secondPlanLine in planChoiceRule.ts). */
    secondPlan?: PlanChoiceWords | null;
    /** The estate leader's words when it is not the plan that leaves the most (leadPlanOf). */
    leadPlan?: PlanChoiceWords | null;
}

/*
 * WORSE THAN STAYING PUT IS SAID — AS AWARENESS, NOT AS A MISTAKE (owner, 19 Oct 2026: "this is not a
 * shock and most of these decisions would be a lifestyle choice but … they need to be aware of this —
 * the positioning is not to make it sound like its a mistake"). The village review found the leading
 * card leaving $109,174 less than staying put under "Best available estate" with nothing saying so,
 * and every plan stopping the Age Pension with nothing saying that either.
 */
const GOAL_METRIC: Record<string, { friendly: string; unit: string; pick: (s: EvaluatedStrategy) => number }> = {
    'Optimal Legacy': { friendly: 'estate', unit: 'in the estate', pick: (s) => s.snapshot?.legacyValue ?? 0 },
    'Optimal Cashflow': { friendly: 'cashflow', unit: 'a year in cashflow', pick: (s) => s.snapshot?.netCashflow ?? 0 },
    'Optimal Age Pension': { friendly: 'Age Pension', unit: 'a year in Age Pension', pick: (s) => s.snapshot?.pension ?? 0 },
};

/** The card's line when it falls short of the baseline on its goal — neutral, with the gap in dollars. */
export function belowBaselineWords(goalLabel: string, strategy: EvaluatedStrategy, baseline: EvaluatedStrategy | null | undefined, baselineWord: string, formatCurrency: (n: number) => string): string | undefined {
    const m = GOAL_METRIC[goalLabel];
    if (!m || !baseline?.snapshot) return undefined;
    const gap = m.pick(baseline) - m.pick(strategy);
    if (!(gap >= 1)) return undefined;
    return `Leaves ${formatCurrency(gap)} less ${m.unit} than ${baselineWord} (${formatCurrency(m.pick(baseline))}). It is the closest of the options modelled on this goal, shown so the trade-off is clear when a move is wanted for other reasons.`;
}

/**
 * One line for the whole field, when it is true of EVERY option: all leave less in the estate than
 * the baseline, and/or none keeps an Age Pension the baseline pays. Null when neither holds.
 */
export function stayPutFieldLine(movers: EvaluatedStrategy[], baseline: EvaluatedStrategy | null | undefined, baselineWord: string, formatCurrency: (n: number) => string): string | null {
    const base = baseline?.snapshot;
    if (!base || movers.length === 0) return null;
    const parts: string[] = [];
    const baseEstate = base.legacyValue ?? 0;
    if (movers.every((s) => (s.snapshot?.legacyValue ?? 0) < baseEstate)) {
        const closest = Math.min(...movers.map((s) => baseEstate - (s.snapshot?.legacyValue ?? 0)));
        parts.push(`Every option modelled leaves less in the estate than ${baselineWord}; the closest leaves ${formatCurrency(closest)} less.`);
    }
    const basePension = base.pension ?? 0;
    if (basePension > 0 && movers.every((s) => (s.snapshot?.pension ?? 0) <= 0)) {
        parts.push(`None of them keeps an Age Pension, which ${baselineWord} pays at ${formatCurrency(basePension)} a year.`);
    }
    if (!parts.length) return null;
    return `${parts.join(' ')} A move like this is often made for the way of life rather than the money, so this is here to be weighed, not as a reason against it.`;
}

/** A card's words from the plan-choice rule: which card, its chip, and its one line. */
export interface PlanChoiceWords { key: string; chip: string; line: string }
const keyOfRow = (s: { id?: unknown; name?: string }) => String(s.id ?? s.name ?? '');

/** The second card's key, chip and one line, from the ranker's output (null when no plan earned one). */
export function secondPlanOf(recommended: ReadonlyArray<RecommendedStrategyEntry> | null | undefined, formatCurrency: (n: number) => string): PlanChoiceWords | null {
    const row = (recommended || []).find((r) => r.label === NEAR_BEST_LABEL && r.second);
    if (!row?.second) return null;
    return { key: keyOfRow(row.strategy as never), chip: secondPlanChip(row.second), line: secondPlanLine(row.second, formatCurrency) };
}

/** The estate leader's chip and line when it is NOT the plan that leaves the most (null otherwise). */
export function leadPlanOf(recommended: ReadonlyArray<RecommendedStrategyEntry> | null | undefined, formatCurrency: (n: number) => string): PlanChoiceWords | null {
    const row = (recommended || []).find((r) => r.label === 'Optimal Legacy' && r.lead);
    if (!row?.lead) return null;
    return { key: keyOfRow(row.strategy as never), chip: leadChip(row.lead), line: leadLine(row.lead, formatCurrency) };
}

/** Swaps a goal chip for the plan-choice rule's words, so the chip says what the card actually is. */
export function applyPlanChoiceWords<C extends { badges: string[]; tradeOffNote?: string }>(card: C, key: string, words: PlanChoiceWords | null, replaces: string): void {
    if (!words || key !== words.key) return;
    card.badges = card.badges.map((b) => (b === replaces ? words.chip : b));
    if (!card.badges.includes(words.chip)) card.badges.unshift(words.chip);
    card.tradeOffNote = words.line;
}

/** Whether a chip belongs under the estate goal: the goal chips plus the rule's own words. */
export const isEstateChip = (b: string): boolean =>
    b === BEST_ESTATE_CHIP || b === 'Best available estate' || b === SECOND_PLAN_CHIP || /^(About the most|Leaves the most)/.test(b);

export function buildGoalCards<S extends EvaluatedStrategy>(input: BuildGoalCardsInput<S>): GoalCard<S>[] {
    const { strategies, goalLeaders, baseline, isBaseline, isSensitivity = () => false, selectedId, baselineWord, formatCurrency, secondPlan = null, leadPlan = null } = input;
    const movers = strategies.filter((s) => !isBaseline(s) && !isSensitivity(s));
    const out: GoalCard<S>[] = [];
    const byId = new Map<string, GoalCard<S>>();
    const keyOf = (s: S) => String((s as { id?: unknown }).id ?? s.name ?? '');
    const push = (strategy: S, badges: string[], belowBaseline?: string) => {
        const key = keyOf(strategy);
        const existing = byId.get(key);
        if (existing) { badges.forEach((b) => { if (!existing.badges.includes(b)) existing.badges.push(b); }); return; }
        const entry: GoalCard<S> = { strategy, badges: [...badges], belowBaseline };
        byId.set(key, entry);
        out.push(entry);
    };

    // A goal leader can still fall short of the baseline (it leads only the movers): say so on the card.
    goalLeaders.forEach((leader) => {
        const labels = leader.recommendationLabels || [];
        const short = labels.map((l) => belowBaselineWords(l, leader, baseline, baselineWord, formatCurrency)).find(Boolean);
        push(leader, labels.map((l) => GOAL_CHIP_LABELS[l] || l), short);
        // The chip says what the card is, and the line why (planChoiceRule.ts): the second card's
        // trade-off, and the estate leader's when it is not the plan that leaves the most.
        const e = byId.get(keyOf(leader));
        if (e) {
            applyPlanChoiceWords(e, keyOf(leader), secondPlan, SECOND_PLAN_CHIP);
            applyPlanChoiceWords(e, keyOf(leader), leadPlan, BEST_ESTATE_CHIP);
        }
    });

    const base = baseline?.snapshot;
    const GOALS = [
        { label: 'Optimal Legacy', friendly: 'estate', pick: (s: S) => s.snapshot?.legacyValue ?? 0, baseValue: base?.legacyValue ?? 0 },
        { label: 'Optimal Cashflow', friendly: 'cashflow', pick: (s: S) => s.snapshot?.netCashflow ?? 0, baseValue: base?.netCashflow ?? 0 },
        { label: 'Optimal Age Pension', friendly: 'Age Pension', pick: (s: S) => s.snapshot?.pension ?? 0, baseValue: base?.pension ?? 0 },
    ] as const;
    GOALS.forEach(({ label, friendly, pick, baseValue }) => {
        if (movers.length === 0) return;
        if (goalLeaders.some((l) => (l.recommendationLabels || []).includes(label))) return;
        // Every option at nil pension is a finding, not a shortlist — the "what we modelled" note
        // says so, and a "best available $0" card would add nothing.
        if (label === 'Optimal Age Pension' && movers.every((s) => (s.snapshot?.pension ?? 0) <= 0)) return;
        const best = movers.reduce((top, s) => (pick(s) > pick(top) ? s : top), movers[0]!);
        push(best, [`Best available ${friendly}`], belowBaselineWords(label, best, baseline, baselineWord, formatCurrency)
            ?? `Does not beat ${baselineWord} (${formatCurrency(baseValue)}); the closest of the options modelled on this goal.`);
    });

    if (selectedId && !byId.has(String(selectedId))) {
        const chosen = movers.find((s) => keyOf(s) === String(selectedId));
        if (chosen) push(chosen, ['Your selection']);
    }
    return out;
}

/** The cards the chosen chip shows; a chosen card that leads nothing shows under every chip. */
export function visibleGoalCards<S extends EvaluatedStrategy>(cards: GoalCard<S>[], focus: GoalFocus, coveredSameAsEstate: boolean): GoalCard<S>[] {
    // The same plan as Best estate is said in one line, not shown twice (owner, 24 Sep 2026).
    if (focus === 'covered' && coveredSameAsEstate) return [];
    const badgesFor: Record<GoalFocus, string[]> = {
        covered: [CASHFLOW_COVERED_CHIP],
        legacy: [BEST_ESTATE_CHIP, 'Best available estate', SECOND_PLAN_CHIP],
        cashflow: ['Best cashflow', 'Best available cashflow'],
        pension: ['Best Age Pension', 'Best available Age Pension'],
    };
    return cards.filter((c) => c.badges.some((b) => badgesFor[focus].includes(b) || (focus === 'legacy' && isEstateChip(b)) || b === 'Your selection'));
}

/**
 * The one line under the chips when a goal has NO card at all — why, in the client's words. A
 * missing "Best Age Pension" card is a real finding for a client above the assets cut-off; left
 * unsaid it reads as though the goal was never considered. (The longer "what we modelled for each
 * goal" box was removed on 3 Oct 2026: the winners are the cards, and the assumptions live on the
 * Assumptions page and in the advice document.)
 */
export function emptyGoalLine<S extends EvaluatedStrategy>(focus: GoalFocus, movers: S[], baselineWord: string): string {
    if (focus === 'pension' && movers.length > 0 && movers.every((s) => (s.snapshot?.pension ?? 0) <= 0)) {
        return 'No option modelled produces any Age Pension — the assessable assets stay above the cut-off in every one, so there is no pension to improve. That is a finding, not a gap.';
    }
    return `No option improved on ${baselineWord}, so nothing is recommended for this goal.`;
}
